import { useMemo } from 'react';
import { PermissionEnum, type CurrentMemberFragment } from 'generated/graphql';
import type { User } from '~/server/types';
import { isUserLoggedIn, useUser } from './use-user';

/**
 * Get the permissions for a user against a specific professional, as a team
 * member of a professional profile.
 * @param teamMembers The team member data.
 * @param professional The professional (ID or URLSegment).
 * @returns The permissions for the user as a team member of the professional.
 */
function collectPermissions(
    teamMembers: CurrentMemberFragment['TeamMembers'],
    professional: string | number
): PermissionEnum[] {
    if (!professional) {
        return [];
    }

    const team = teamMembers
        .filter((team) => {
            if (typeof professional === 'string') {
                return team.Professional.URLSegment === professional;
            } else {
                return team.Professional.ID === professional;
            }
        })
        .pop();

    if (!team) {
        return [];
    }

    const professionalPermissions = team.Professional.Permissions ?? [];

    return [...team.Permissions, ...professionalPermissions];
}

/**
 * Check if a user has the required permission(s). If a professional is passed,
 * check if the user has the required permissions(s) for that specific professional.
 * @param user The user to check the permissions for.
 * @param permissions The permission(s) to check for.
 * @param professional An optional professional (ID or URLSegment) to check the permissions for.
 * @returns The user has the required permission(s).
 */
export function getPermissions(
    user: User | null | undefined,
    permissions: PermissionEnum | PermissionEnum[],
    professional?: string | number | null
): boolean {
    if (!user || !isUserLoggedIn(user)) {
        return false;
    }
    const { Permissions, TeamMembers } = user;
    const permissionsToCheck = Array.isArray(permissions)
        ? permissions
        : [permissions];

    // Check the user's root level permissions.
    const membersPermissions = [...Permissions];

    if (professional) {
        // If a professional is passed, check if the user has the required
        // permissions for that specific professional.
        membersPermissions.push(
            ...collectPermissions(TeamMembers, professional)
        );
    }

    return permissionsToCheck.every((permission) =>
        membersPermissions.includes(permission)
    );
}

/**
 * Hook to check if the current user has the required permission(s). If a
 * professional is passed, check if the user has the required permissions(s)
 * for that specific professional.
 * @param permissions The permission(s) to check for.
 * @param professional An optional professional (ID or URLSegment) to check the permissions for.
 * @returns The current user has the required permission(s).
 */
export function usePermissions(
    permissions: PermissionEnum | PermissionEnum[],
    professional?: string | number | null
): boolean {
    const user = useUser();

    return useMemo(
        () => getPermissions(user, permissions, professional),
        [user, permissions, professional]
    );
}

/**
 * Hook to check if the current user has certain root level permissions.
 * @returns The current user's root level permissions.
 */
export function useRootPermissions() {
    const editProfile = usePermissions(PermissionEnum.BmProfileEdit);
    const editAdvertAdmin = usePermissions(PermissionEnum.BmAdvertAdminEdit);
    return useMemo(() => {
        return {
            editProfile,
            editAdvertAdmin,
        };
    }, [editProfile, editAdvertAdmin]);
}

/**
 * Hook to check if the current user has permissions to edit a professional
 * and/or their items.
 * @param professional The professional (ID or URLSegment) to check the permissions for.
 * @returns The current user's edit permissions.
 */
export function useEditPermissions(professional: string | number) {
    const canEditProfile = usePermissions(
        PermissionEnum.BmProfileEdit,
        professional
    );
    const canEditProducts = usePermissions(
        PermissionEnum.BmProductsEdit,
        professional
    );
    const canEditProjects = usePermissions(
        PermissionEnum.BmProjectEdit,
        professional
    );
    const canEditArticles = usePermissions(
        PermissionEnum.BmArticleEdit,
        professional
    );
    return useMemo(() => {
        return {
            canEditProfile,
            canEditProducts,
            canEditProjects,
            canEditArticles,
        };
    }, [canEditProfile, canEditProducts, canEditProjects, canEditArticles]);
}
